import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useData as useDataVike } from 'vike-react/useData';

import { request } from '@/renderer/utils/api';
import { useEffectOnce } from '@/renderer/utils/hooks/useEffectOnce';
import { useRefUpdating } from '@/renderer/utils/hooks/useRefUpdating';
import { useSsr } from '@/renderer/utils/hooks/useSSr';
import { useUpdateEffect } from '@/renderer/utils/hooks/useUpdateEffect';
import { IS_SERVER } from '@/renderer/utils/ssr';
import { authToken } from '@/renderer/utils/useMe';
import { RasContextStore } from '@/renderer/utils/useRasContext';
import type { RequestSchema } from '@/types/request';

type Expired = { expired: true };

function isExpired<T>(data: T | { expired: true }): data is Expired {
  return (data as Expired)?.expired === true;
}

function rasContextReducer(
  state: RasContextData,
  {
    action,
    data,
  }:
    | { action: 'RESET'; data: RasContextData }
    | { action: 'user'; data: { user: API.User | null; authToken?: string | null | undefined } }
    | { action: 'SET'; data: Partial<Omit<RasContextData, 'user'>> },
) {
  // @todo this way of setting authToken here sucks, find a better way?
  if (action === 'user') {
    if (!state) {
      state = {};
    }

    if (data?.authToken || data?.authToken === null) {
      state.authToken = data.authToken;

      if (data.authToken) {
        if (authToken() !== data.authToken) {
          authToken(data.authToken);
        }
      } else if (authToken()) {
        authToken(null);
      }
    }

    if (data?.user || data?.user === null) {
      const handler: ProxyHandler<RasContextData> = {
        get(target, prop) {
          if (prop === 'user') {
            return data.user;
          }

          return Reflect.get(target, prop);
        },
      };

      return new Proxy(state, handler);
    }
  } else if (action === 'RESET') {
    if (data?.authToken) {
      if (authToken() !== data.authToken) {
        authToken(data.authToken);
      }
    } else if (data?.authToken === null && authToken()) {
      authToken(null);
    } else if (data?.authToken === undefined && data?.user === null) {
      authToken(null);
    }

    return data;
  } else if (action === 'SET' && data) {
    let changed = 0;

    if (!state) {
      state = {};
      changed = -1;
    }

    for (const k in data) {
      // Never let SET touch `user`. `user` is owned exclusively by the 'user' and
      // 'RESET' actions. A SET payload can legitimately carry `user: undefined`
      // (e.g. a page whose +data merges dataguard's client result — which is
      // user-less until `userPromise` resolves — with its page data), and writing
      // that here would blank a still-valid signed-in session (#1552).
      if (k === 'user') {
        continue;
      }

      const key = k as keyof Omit<RasContextData, 'user'>;
      // @todo why did we get a 'seasons' error here?
      if (state[key] !== data[key]) {
        state[key] = data[key];
        changed = changed === 0 ? 1 : changed;
      }
    }

    if (data?.authToken || data?.authToken === null) {
      if (data.authToken) {
        if (authToken() !== data.authToken) {
          authToken(data.authToken);
        }
      } else if (authToken()) {
        authToken(null);
      }
    }

    if (changed === 1) {
      // Preserve the current user explicitly. When `me` was populated via the
      // 'user' action (the native path — see setUser), `state` is a Proxy whose
      // `user` is a getter trap, not an own enumerable key, so a bare `{ ...state }`
      // would silently drop it. Reading `state.user` resolves the trap and
      // re-attaches the user as a real own key on the returned object (#1552).
      return { ...state, user: state.user };
    }
  }

  return state;
}

export function RasContextProvider({ children }: { children: React.ReactNode }) {
  const data = useDataVike<RasContextData>();
  const [currentContext, dispatch] = useReducer(rasContextReducer, data);

  const { isBrowser } = useSsr();
  const isBrowserRef = useRefUpdating(isBrowser);
  // `currentContext` IS the RasContextData (it's the reducer state), so the user
  // lives at `currentContext.user`, not `currentContext.data.user`. The old
  // `.data.user` read was always undefined, which permanently defeated the
  // RESET-vs-SET guard below and the "recover a missing user" branch (#1552).
  const userRef = useRefUpdating(currentContext?.user);

  // Guards a single in-flight recovery fetch (below) so a run of user-less data
  // objects can't stack duplicate /api/v1/user requests.
  const recoveringUserRef = useRef(false);

  // Last-resort recovery: if we end up on the client with a valid token but no
  // user in the store, fetch it and set it. In the normal flow dataguard sets
  // `userPromise` whenever a token is present (so `data` is defined and the
  // effect below handles it); this only fires for edge cases that reach the store
  // user-less while still holding a session (e.g. a route that bypasses dataguard).
  // It never clears `me` — refreshToken / PageTokenRefresher own logout semantics
  // (#1464 / #1216) — so a transient failure just leaves the user unknown.
  const recoverUser = useCallback(async () => {
    if (IS_SERVER || recoveringUserRef.current || !authToken()) {
      return;
    }

    recoveringUserRef.current = true;

    try {
      const response = await request(
        { input: '/api/v1/user' } as RequestSchema<
          API.WithData<API.User | null> | { expired: true }
        >,
        { withResponse: true, cache: { maxAge: 10 * 1000, on: 'client' } },
      );

      const body = (response as { data?: API.WithData<API.User | null> | { expired: true } })?.data;

      if (!isExpired(body) && body?.data) {
        const user = body.data;
        flushSync(() => dispatch({ action: 'user', data: { user } }));
        Sentry.setUser({ id: user?.id });
      }
    } catch {
      // Transient/auth failure: leave `me` as-is (unknown). Do not force a logout.
    } finally {
      recoveringUserRef.current = false;
    }
  }, []);

  useUpdateEffect(() => {
    if (data === undefined) {
      if (userRef.current === undefined && isBrowserRef.current) {
        // We don't have a user (and no page data told us to reset). If a session
        // token is present, recover the user rather than sitting blank.
        recoverUser();
      }
    } else if (data && (!data.userPromise || Object.keys(data).length !== 1)) {
      // in SSR, data is always an object. in CSR, we want to ignore this.
      if (data.user !== undefined && userRef.current === undefined) {
        // reset if we are adding new user state
        dispatch({ action: 'RESET', data });
      } else {
        // or just set if we don't have new user data to change
        dispatch({ action: 'SET', data });
      }
    }
  }, [data, data?.user, recoverUser]);

  useEffect(() => {
    const theData = data;
    (async () => {
      if (theData?.userPromise) {
        const { userPromise, ...otherData } = theData;

        const userData = await userPromise;

        if (!isExpired(userData?.data) && userData?.data?.data) {
          flushSync(() =>
            dispatch({ action: 'RESET', data: { ...otherData, user: userData.data.data } }),
          );
        }
      }
    })();
  }, [data, data?.userPromise]);

  const setUser = useCallback(
    (user: API.User | null, authTokenValue?: string | null | undefined) => {
      flushSync(() => dispatch({ action: 'user', data: { user, authToken: authTokenValue } }));
      Sentry.setUser({ id: user?.id });
    },
    [],
  );

  const setData = useCallback<NonNullable<RasContext['setData']>>(
    (key, value) => dispatch({ action: 'SET', data: { [key]: value } }),
    [],
  );

  useEffectOnce(() => {
    Sentry.setUser({ id: data?.user?.id });
  });

  const context = useMemo<RasContext>(
    () => ({
      data: currentContext,
      setData,
      setUser,
    }),
    [currentContext, setData, setUser],
  );

  return <RasContextStore.Provider value={context}>{children}</RasContextStore.Provider>;
}
